import React, { useMemo, useState } from 'react';
import { Building, IdCard } from 'lucide-react';
import { TextInput } from '../../../shared/components/TextInput';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { RadioGroup } from '../../../shared/components/RadioGroup';
import {
  maskCNPJ,
  maskCPF,
  onlyCnpjChars,
  validateCNPJ,
  validateCPF,
} from '../../../shared/utils/validators';
import type {
  Classificacao,
  CreateClientePayload,
  Origem,
  Porte,
  SegmentoMercado,
  TipoPessoa,
} from '../api';

interface Props {
  mode: 'create' | 'edit';
  initial?: Partial<CreateClientePayload> & {
    tipoPessoa?: TipoPessoa;
    documento?: string;
  };
  submitting?: boolean;
  submitError?: string | null;
  /**
   * No modo `edit`, permite editar o CNPJ/CPF (válvula de escape à
   * RN-COM-001 para corrigir digitação do cadastro inicial). Só admin
   * deveria receber `true`. Ignorado no modo `create` (CNPJ é sempre
   * editável na criação).
   */
  canEditDocumento?: boolean;
  onSubmit: (payload: CreateClientePayload) => Promise<void> | void;
  onCancel?: () => void;
}

const SEGMENTOS: { value: SegmentoMercado; label: string }[] = [
  { value: 'tecnologia', label: 'Tecnologia' },
  { value: 'financeiro', label: 'Financeiro' },
  { value: 'saude', label: 'Saúde' },
  { value: 'educacao', label: 'Educação' },
  { value: 'industria', label: 'Indústria' },
  { value: 'varejo', label: 'Varejo' },
  { value: 'servicos', label: 'Serviços' },
  { value: 'governo', label: 'Governo' },
  { value: 'agronegocio', label: 'Agronegócio' },
  { value: 'construcao', label: 'Construção' },
  { value: 'outros', label: 'Outros' },
];

const PORTES: { value: Porte; label: string }[] = [
  { value: 'mei', label: 'MEI' },
  { value: 'me', label: 'ME' },
  { value: 'epp', label: 'EPP' },
  { value: 'medio', label: 'Médio' },
  { value: 'grande', label: 'Grande' },
];

const CLASSIFICACOES: { value: Classificacao; label: string }[] = [
  { value: 'estrategico', label: 'Estratégico' },
  { value: 'premium', label: 'Premium' },
  { value: 'corporativo', label: 'Corporativo' },
  { value: 'governo', label: 'Governo' },
  { value: 'pequeno_porte', label: 'Pequeno Porte' },
  { value: 'prospect', label: 'Prospect' },
];

const ORIGENS: { value: Origem; label: string }[] = [
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'indicacao', label: 'Indicação' },
  { value: 'site', label: 'Site' },
  { value: 'evento', label: 'Evento' },
  { value: 'licitacao', label: 'Licitação' },
  { value: 'parceiro', label: 'Parceiro' },
  { value: 'prospeccao_ativa', label: 'Prospecção Ativa' },
];

export const ClienteForm: React.FC<Props> = ({
  mode,
  initial,
  submitting,
  submitError,
  canEditDocumento = false,
  onSubmit,
  onCancel,
}) => {
  const [tipoPessoa, setTipoPessoa] = useState<TipoPessoa>(initial?.tipoPessoa ?? 'PJ');
  const [razaoSocial, setRazaoSocial] = useState(initial?.razaoSocial ?? '');
  const [nomeFantasia, setNomeFantasia] = useState(initial?.nomeFantasia ?? '');
  const [documento, setDocumento] = useState(initial?.documento ?? '');
  const [inscricaoEstadual, setInscricaoEstadual] = useState(initial?.inscricaoEstadual ?? '');
  const [inscricaoMunicipal, setInscricaoMunicipal] = useState(initial?.inscricaoMunicipal ?? '');
  const [dataFundacao, setDataFundacao] = useState(initial?.dataFundacao ?? '');
  const [cnaePrincipal, setCnaePrincipal] = useState(initial?.cnaePrincipal ?? '');
  const [segmentoMercado, setSegmentoMercado] = useState<SegmentoMercado | ''>(
    initial?.segmentoMercado ?? '',
  );
  const [porte, setPorte] = useState<Porte | ''>(initial?.porte ?? '');
  const [classificacao, setClassificacao] = useState<Classificacao | ''>(
    initial?.classificacao ?? '',
  );
  const [origem, setOrigem] = useState<Origem | ''>(initial?.origem ?? '');

  const [docTouched, setDocTouched] = useState(false);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);

  const isEdit = mode === 'edit';
  const isPj = tipoPessoa === 'PJ';
  const documentoDisabled = isEdit && !canEditDocumento;

  const docFormatado = useMemo(
    () => (isPj ? maskCNPJ(documento) : maskCPF(documento)),
    [isPj, documento],
  );

  const errors = useMemo(() => {
    const e: Record<string, string> = {};
    if (!razaoSocial.trim()) e.razaoSocial = isPj ? 'Razão Social é obrigatória.' : 'Nome completo é obrigatório.';
    if (!documento.trim()) e.documento = isPj ? 'CNPJ é obrigatório.' : 'CPF é obrigatório.';
    else if (isPj && !validateCNPJ(documento)) e.documento = 'CNPJ inválido. Verifique os dígitos verificadores.';
    else if (!isPj && !validateCPF(documento)) e.documento = 'CPF inválido. Verifique os dígitos verificadores.';
    if (!segmentoMercado) e.segmentoMercado = 'Segmento de mercado é obrigatório.';
    return e;
  }, [razaoSocial, documento, isPj, segmentoMercado]);

  const handleSubmit = async (evt: React.FormEvent) => {
    evt.preventDefault();
    setAttemptedSubmit(true);
    setDocTouched(true);
    if (Object.keys(errors).length > 0) return;
    // PJ: normaliza como alfanumérico uppercase (preparado para CNPJ
    // Alfanumérico — IN RFB 2.229/2024). PF: só dígitos.
    const documentoNormalizado = isPj
      ? onlyCnpjChars(documento)
      : documento.replace(/[^\d]/g, '');
    const payload: CreateClientePayload = {
      tipoPessoa,
      razaoSocial: razaoSocial.trim(),
      documento: documentoNormalizado,
      segmentoMercado: segmentoMercado as SegmentoMercado,
    };
    if (nomeFantasia.trim()) payload.nomeFantasia = nomeFantasia.trim();
    if (isPj && inscricaoEstadual.trim()) payload.inscricaoEstadual = inscricaoEstadual.trim();
    if (isPj && inscricaoMunicipal.trim()) payload.inscricaoMunicipal = inscricaoMunicipal.trim();
    if (dataFundacao) payload.dataFundacao = dataFundacao;
    if (isPj && cnaePrincipal.trim()) payload.cnaePrincipal = cnaePrincipal.trim();
    if (isPj && porte) payload.porte = porte;
    if (classificacao) payload.classificacao = classificacao;
    if (origem) payload.origem = origem;
    await onSubmit(payload);
  };

  const showErr = (key: string) => attemptedSubmit && errors[key] ? errors[key] : undefined;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <RadioGroup
        legend="Tipo de pessoa"
        name="tipoPessoa"
        options={[
          { value: 'PJ', label: 'Pessoa Jurídica', disabled: isEdit },
          { value: 'PF', label: 'Pessoa Física', disabled: isEdit },
        ]}
        value={tipoPessoa}
        onChange={(v) => setTipoPessoa(v as 'PJ' | 'PF')}
      />

      <TextInput
        label={isPj ? 'Razão Social' : 'Nome Completo'}
        icon={<Building size={16} />}
        value={razaoSocial}
        onChange={(e) => setRazaoSocial(e.target.value)}
        error={showErr('razaoSocial')}
        required
      />

      {isPj && (
        <TextInput
          label="Nome Fantasia"
          value={nomeFantasia}
          onChange={(e) => setNomeFantasia(e.target.value)}
        />
      )}

      <TextInput
        label={isPj ? 'CNPJ' : 'CPF'}
        icon={<IdCard size={16} />}
        value={docFormatado}
        onChange={(e) => setDocumento(e.target.value)}
        onBlur={() => setDocTouched(true)}
        error={(docTouched || attemptedSubmit) ? errors.documento : undefined}
        disabled={documentoDisabled}
        required
      />

      {isPj && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <TextInput
            label="Inscrição Estadual"
            value={inscricaoEstadual}
            onChange={(e) => setInscricaoEstadual(e.target.value)}
          />
          <TextInput
            label="Inscrição Municipal"
            value={inscricaoMunicipal}
            onChange={(e) => setInscricaoMunicipal(e.target.value)}
          />
        </div>
      )}

      <TextInput
        label={isPj ? 'Data de Fundação' : 'Data de Nascimento'}
        type="date"
        value={dataFundacao}
        onChange={(e) => setDataFundacao(e.target.value)}
      />

      {isPj && (
        <TextInput
          label="CNAE Principal"
          value={cnaePrincipal}
          onChange={(e) => setCnaePrincipal(e.target.value)}
        />
      )}

      <AutocompleteSelect
        label="Segmento de Mercado *"
        options={SEGMENTOS}
        value={segmentoMercado}
        onChange={(v) => setSegmentoMercado(v as SegmentoMercado)}
        error={showErr('segmentoMercado')}
      />

      {isPj && (
        <AutocompleteSelect
          label="Porte"
          options={PORTES}
          value={porte}
          onChange={(v) => setPorte(v as Porte)}
        />
      )}

      <AutocompleteSelect
        label="Classificação"
        options={CLASSIFICACOES}
        value={classificacao}
        onChange={(v) => setClassificacao(v as Classificacao)}
      />

      <AutocompleteSelect
        label="Origem"
        options={ORIGENS}
        value={origem}
        onChange={(v) => setOrigem(v as Origem)}
      />

      {submitError && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {submitError}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancelar
          </button>
        )}
        <button
          type="submit"
          disabled={submitting}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? 'Salvando…' : isEdit ? 'Salvar alterações' : 'Cadastrar cliente'}
        </button>
      </div>
    </form>
  );
};
